import { Row, Col, Card, Button, Typography, Alert } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Loader from "../components/loader";

const ApiTokens = () => {
  const { Title, Paragraph, Text } = Typography;
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState("");
  const [createdToken, setCreatedToken] = useState("");
  const [hasToken, setHasToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  const TOKEN_LENGTH = 64;
  const TOKEN_SETS = {
    upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    lower: "abcdefghijklmnopqrstuvwxyz",
    digits: "0123456789",
    symbols: "!@#$%^&*()-_=+[]{}|;:,.<>?/",
  };

  const getRandomInt = (max) => {
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      return array[0] % max;
    }
    return Math.floor(Math.random() * max);
  };

  const shuffle = (chars) => {
    for (let i = chars.length - 1; i > 0; i -= 1) {
      const j = getRandomInt(i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars;
  };

  const generateToken = () => {
    const required = [
      TOKEN_SETS.upper[getRandomInt(TOKEN_SETS.upper.length)],
      TOKEN_SETS.lower[getRandomInt(TOKEN_SETS.lower.length)],
      TOKEN_SETS.digits[getRandomInt(TOKEN_SETS.digits.length)],
      TOKEN_SETS.symbols[getRandomInt(TOKEN_SETS.symbols.length)],
    ];
    const allChars = Object.values(TOKEN_SETS).join("");
    const remaining = [];
    for (let i = required.length; i < TOKEN_LENGTH; i += 1) {
      remaining.push(allChars[getRandomInt(allChars.length)]);
    }
    return shuffle([...required, ...remaining]).join("");
  };

  const fetchUserToken = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://cms.paysmart.co.zm/users/me?fields=id,token",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userData = response.data.data || {};
      setUserId(userData.id || "");
      setHasToken(Boolean(userData.token));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        history.push("/sign-in");
      }
    }
  };

  const handleCreateToken = async () => {
    try {
      setSaving(true);
      setTokenCopied(false);
      const newToken = generateToken();
      await axios.patch(
        `https://cms.paysmart.co.zm/users/${userId}`,
        { token: newToken },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCreatedToken(newToken);
      setHasToken(true);
      setSaving(false);
    } catch (error) {
      setSaving(false);
      if (error.response && error.response.status === 401) {
        history.push("/sign-in");
      }
    }
  };

  const handleCopyToken = async () => {
    if (!createdToken) {
      return;
    }
    await navigator.clipboard.writeText(createdToken);
    setCreatedToken("");
    setTokenCopied(true);
  };

  useEffect(() => {
    fetchUserToken();
  }, []);

  return (
    <div className="tabled api-tokens-page">
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24} xl={14}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24 api-tokens-hero"
            >
              <div className="api-tokens-header">
                <div>
                  <Text className="api-tokens-kicker">API Access</Text>
                  <Title level={3} className="api-tokens-title">
                    Your API Token
                  </Title>
                </div>
              </div>
              <div className="api-tokens-callout">
                <Paragraph className="api-tokens-intro">
                  Each account can have exactly one API token. Tokens never
                  expire and are shown only once. Copy it now and store it
                  securely.
                </Paragraph>
              </div>
              {createdToken ? (
                <Alert
                  type="success"
                  showIcon
                  message="Token generated"
                  description={
                    <div>
                      <Paragraph style={{ marginBottom: 8 }}>
                        <Text code>{createdToken}</Text>
                      </Paragraph>
                      <Button
                        type="primary"
                        onClick={handleCopyToken}
                        className="api-tokens-button"
                      >
                        Copy token once
                      </Button>
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <Card
                  bordered={false}
                  className="api-tokens-status-card api-tokens-delay-1"
                  style={{
                    background:
                      "linear-gradient(135deg, #f6f9ff 0%, #eef2f7 100%)",
                    marginBottom: 16,
                    padding: "4px 8px",
                  }}
                >
                  <Title level={5} style={{ marginBottom: 8 }}>
                    Token status
                  </Title>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {hasToken || tokenCopied
                      ? "Your token is set. For security, it is hidden."
                      : "No token yet. Generate one to start using the API."}
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    <Text code>********</Text>
                  </Paragraph>
                </Card>
              )}
            </Card>
          </Col>
          <Col xs={24} xl={10}>
            <Card
              bordered={false}
              className="criclebox mb-24 api-tokens-side-card api-tokens-delay-1"
            >
              <Title level={5}>Use it in requests</Title>
              <Paragraph>
                Include your token in the Authorization header when calling the
                API.
              </Paragraph>
              <Paragraph copyable>
                Authorization: Bearer &lt;your-token&gt;
              </Paragraph>
              <Paragraph>
                If you lose a token, generate a new one here.
              </Paragraph>
            </Card>
            <Card
              bordered={false}
              className="criclebox mb-24 api-tokens-side-card api-tokens-delay-2"
            >
              <Title level={5}>Good to know</Title>
              <Paragraph>
                This token never expires. Keep it private and store it in a
                password manager or secret vault.
              </Paragraph>
              <Paragraph>
                Need a rotation? Generate a new token and update your services
                immediately.
              </Paragraph>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ApiTokens;
